/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import {
    fork, takeLatest, call, select, put,
} from 'redux-saga/effects';
import mbpLogger from 'mbp-logger';
import mbpUtil from 'mbp-api-util';
import sdkFeatureFlags from 'feature-flags-sdk';
import configClient from 'mbp-config-server-api';

import * as helper from './App-Helper';
import {
    loadApp,
    setFeatureFlag, setBrandConfig,
} from './App-Actions';
import {
    getProfileInfo,
    getUserType,
    getUserRole,
} from '../Global/Global-Selectors';
import {
    setProfileEmailId,
    loadedProfileUserRole,
    logUserLoginInSessionReady,
} from '../Global/Global-Actions';

import { getSSRConfig } from '../../../App/App-Selectors';
import memCache from '../../../../../app/helpers/memCache/memCache';

function* getConfig({ config, domain }) {
    try {
        mbpLogger.logDebug({
            config,
            domain,
            function: 'getConfig',
            appName: process.env.npm_package_name,
            module: 'mbp-pwa-ui',
            message: 'Get Config | START',
        });

        const host = (typeof window !== 'undefined') ? mbpUtil.getEnv('APP_WC_HOST') : mbpUtil.getEnv('APP_CONFIG_HOST_SSR');
        const topic = config.topic;
        const branch = config.branch || 'master';

        const cacheKey = host + topic + branch;

        // lookup
        let configResponse = memCache.get(cacheKey);
        if (!configResponse) {
            configResponse = yield call(
                configClient.getConfig,
                {
                    host,
                },
                topic,
                branch,
            );

            if (configResponse.status === 200) {
                memCache.set(cacheKey, configResponse);
            }
        }

        mbpLogger.logDebug({
            host,
            configResponse,
            function: 'getConfig',
            appName: process.env.npm_package_name,
            module: 'mbp-pwa-ui',
            message: 'Config Service Response',
        });

        if (configResponse && configResponse.data && configResponse.data.propertySources.length) {
            const [configPropertySource] = configResponse.data.propertySources;

            mbpLogger.logDebug({
                configPropertySource,
                function: 'getConfig',
                appName: process.env.npm_package_name,
                module: 'mbp-pwa-ui',
                message: 'Config data found.',
            });

            return helper.parseConfig(configPropertySource.source, domain);
        }
    } catch (ex) {
        mbpLogger.logError({
            config,
            domain,
            function: 'getConfig',
            appName: process.env.npm_package_name,
            module: 'mbp-pwa-ui',
            jsError: ex,
            message: 'Failed to get config data from service.',
        });
    }

    return {};
}

function* onLoadConfig({ branch, domain }) {
    let config = {};
    let topic = 'ref-brand';

    try {
        // Uat2 have different repo that listen the mastr brand
        const appGraphqlEnv = mbpUtil.getEnv('APP_VERSION_ENV');
        if (appGraphqlEnv === 'uat2') {
            topic = 'ref-brand-beta';
        }

        mbpLogger.logDebug({
            topic,
            branch,
            function: 'onLoadConfig',
            appName: process.env.npm_package_name,
            module: 'mbp-pwa-ui',
            message: 'Load Config | START',
        });

        config = yield call(getConfig, {
            config: {
                topic,
                branch,
            },
            domain,
        });

        if (Object.keys(config).length > 0) {
            config = {
                ...config,
                id: config['product-brand-id'],
                code: config['brand-id'],
                domain: config['domain-name'],
                storeId: config['store-id'],
            };
        }

        yield put(setBrandConfig(config));
    } catch (ex) {
        // Uat2 have different repo that listen the mastr brand
        const appGraphqlEnv = mbpUtil.getEnv('APP_VERSION_ENV');
        if (appGraphqlEnv === 'uat2') {
            topic = 'ref-brand-beta';
        }

        mbpLogger.logError({
            topic,
            branch,
            function: 'onLoadConfig',
            appName: process.env.npm_package_name,
            module: 'mbp-pwa-ui',
            jsError: ex,
            message: 'Failed to load config data.',
        });
    }

    return config;
}

function* getFlags(brand) {
    let flags = {};
    const isSSR = (typeof window === 'undefined');
    const profile = yield select(getProfileInfo);
    const device = helper.discoverDeviceType();
    const browser = helper.getBrowser();
    const operatingSystem = helper.getOperatingSystem();
    const userType = yield select(getUserType);
    const userStatus = yield select(getUserRole);
    const email = (profile.email) ? profile.email : '';
    const { userKey } = yield select(getSSRConfig);
    let sessionUserKey = userKey;

    if (!isSSR) {
        sessionUserKey =  helper.getUserKey(sessionUserKey);
    }

    if (sessionUserKey) {
        const payload = {
            userKey: sessionUserKey || 'anonymous',
            userType: userType || 'G',
            userStatus,
            email,
            firstName: profile.firstName,
            lastName: profile.lastName,
            browser,
            operatingSystem,
            brand,
            version: 'rel',
            device,
        };

        // Uat2 have different repo that listen the flags
        const appGraphqlEnv = mbpUtil.getEnv('APP_VERSION_ENV');
        let topicFlag = 'mbp-ui';
        if (appGraphqlEnv === 'uat2') {
            topicFlag = 'mbp-ui-beta';
        }

        const featureFlagsResponse = yield call(sdkFeatureFlags.getFeatureFlags, {}, topicFlag, payload);

        flags = featureFlagsResponse.data;
    }
    return flags;
}

function* onLoadFlags({
    brand,
}) {
    const topic = 'app-feature-flags';
    let flags = {};

    try {
        mbpLogger.logDebug({
            brand,
            function: 'onLoadFlags',
            appName: process.env.npm_package_name,
            module: 'mbp-pwa-ui',
            message: 'Load Flag | START',
        });
        if (brand) {
            flags = yield call(getFlags, brand);

            yield put(setFeatureFlag(flags));
        }
    } catch (ex) {
        mbpLogger.logError({
            topic,
            brand,
            function: 'onLoadFlags',
            appName: process.env.npm_package_name,
            module: 'mbp-pwa-ui',
            jsError: ex,
            message: 'Failed to load flag data.',
        });
    }

    return flags;
}

function* workerLoadApp() {
    try {
        const host = (typeof window !== 'undefined') ? window.location.hostname : '';
        const brandDomain = helper.getBrandDomain(host);

        yield fork(onLoadConfig, { domain: brandDomain });

        yield fork(onLoadFlags, { brand: brandDomain });
    } catch (ex) {
        mbpLogger.logError({
            function: 'workerLoadApp',
            appName: process.env.npm_package_name,
            module: 'mbp-pwa-ui',
            jsError: ex,
            message: 'load App - Checkout',
        });
    }
}

function* watchLoadApp() {
    yield takeLatest([
        loadApp().type,
    ], workerLoadApp);
}

function* workerGetFeatureFlags() {
    const host = (typeof window !== 'undefined') ? window.location.hostname : '';
    const brandDomain = helper.getBrandDomain(host);
    yield fork(onLoadFlags, { brand: brandDomain });
}

function* watchUserProfileChangeGetFeatureFlags() {
    yield takeLatest([
        setProfileEmailId().type,
        loadedProfileUserRole().type,
        logUserLoginInSessionReady().type,
    ], workerGetFeatureFlags);
}

const watchers = [
    fork(watchLoadApp),
    fork(watchUserProfileChangeGetFeatureFlags),
];

export {
    watchers,
};

export default {};
